// Thin LOCAL fetch client for reading the patient's pre-visit INTAKE (the FB-authorized demo clinical
// store, demo_visit_intake — the answers a member gives in booking step 01). Same shape as the other
// THU-346 local clients: baseUrl + getToken → Bearer, credentials:"include", unwraps { data }. The
// answers are decrypted server-side and RLS-scoped to the assigned doctor.
import { useMemo } from "react";
import { API_ORIGIN } from "../config";
import { useAppAuth } from "../auth";
import type { VisitCategory } from "./visitCategory";

export interface IntakeAnswer {
  q: string;
  a: string;
}

export interface IntakeRecord {
  id: string;
  category: VisitCategory; // medical | dental | vision (older rows default to medical server-side)
  form_key: string;
  form_title: string;
  responses: IntakeAnswer[];
  created_at: string;
}

export interface IntakeApi {
  list(patientId: string): Promise<IntakeRecord[]>;
}

export function useIntakeApi(): IntakeApi {
  const { getToken } = useAppAuth();
  return useMemo<IntakeApi>(() => {
    return {
      async list(patientId: string) {
        const headers: Record<string, string> = {};
        const token = await getToken();
        if (token) headers["Authorization"] = `Bearer ${token}`;
        const res = await fetch(
          `${API_ORIGIN}/v1/doctor/patients/${encodeURIComponent(patientId)}/intake`,
          { method: "GET", headers, credentials: "include" },
        );
        const text = await res.text();
        const json: unknown = text ? JSON.parse(text) : {};
        if (!res.ok) {
          const err = (json as { error?: { message?: string } }).error;
          throw new Error(err?.message ?? "Request failed");
        }
        return (json as { data?: { intakes?: IntakeRecord[] } }).data?.intakes ?? [];
      },
    };
  }, [getToken]);
}
